import { useAuth } from '@/context/AuthContext';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/useEntityOperations';
import { Database } from '@/types/supabase';
import { RentalKit, RentalKitItem } from '@/types/entities/rental-kit';

type DbRentalKitRow = Database['public']['Tables']['rental_kits']['Row'];

// Mapper: Database → Frontend (snake_case → camelCase)
const dbRentalKitToRentalKit = (dbItem: DbRentalKitRow): RentalKit => {
  // Parse items from JSON, supporting legacy shapes
  let items: RentalKitItem[] = [];
  if (dbItem.items) {
    try {
      const raw = typeof dbItem.items === 'string'
        ? JSON.parse(dbItem.items)
        : dbItem.items;

      // Normalize to an array of plain objects
      const rawArray: unknown[] = Array.isArray(raw)
        ? raw
        : (typeof raw === 'object' && raw !== null && Array.isArray((raw as { items?: unknown }).items))
          ? ((raw as { items?: unknown[] }).items ?? [])
          : [];

      // Map various historical field names to current RentalKitItem shape
      items = rawArray
        .map((it) => {
          if (it && typeof it === 'object') {
            const obj = it as Record<string, unknown>;
            const idCandidate =
              (typeof obj.id === 'string' ? obj.id : undefined) ??
              (typeof (obj as Record<string, unknown>).inventory_id === 'string'
                ? ((obj as Record<string, unknown>).inventory_id as string)
                : undefined) ??
              (typeof (obj as Record<string, unknown>)['itemId'] === 'string'
                ? ((obj as Record<string, unknown>)['itemId'] as string)
                : undefined) ??
              (typeof (obj as Record<string, unknown>)['inventoryId'] === 'string'
                ? ((obj as Record<string, unknown>)['inventoryId'] as string)
                : undefined);

            if (typeof idCandidate !== 'string' || idCandidate.length === 0) return null;

            const name =
              (typeof obj.name === 'string' ? obj.name : undefined) ??
              (typeof (obj as Record<string, unknown>)['title'] === 'string'
                ? ((obj as Record<string, unknown>)['title'] as string)
                : '') ??
              '';

            const category =
              (typeof obj.category === 'string' ? obj.category : undefined) ??
              (typeof (obj as Record<string, unknown>).category_id === 'string'
                ? ((obj as Record<string, unknown>).category_id as string)
                : undefined) ??
              (typeof (obj as Record<string, unknown>)['categoryId'] === 'string'
                ? ((obj as Record<string, unknown>)['categoryId'] as string)
                : '') ??
              '';

            const qtyCandidate =
              (obj as Record<string, unknown>).quantity ??
              (obj as Record<string, unknown>).count;
            const quantity = typeof qtyCandidate === 'number' ? qtyCandidate : 1;

            return { id: idCandidate, name, category, quantity } as RentalKitItem | null;
          }
          return null;
        })
        .filter((v): v is RentalKitItem => v !== null);
    } catch (error) {
      console.warn('[dbRentalKitToRentalKit] Failed to parse items:', error);
      items = [];
    }
  }

  return {
    id: dbItem.id,
    userId: dbItem.user_id,
    createdAt: new Date(dbItem.created_at || new Date().toISOString()),
    updatedAt: new Date(dbItem.updated_at || new Date().toISOString()),
    name: dbItem.name,
    description: dbItem.description,
    categoryId: dbItem.category_id,
    price: dbItem.price,
    quantity: dbItem.quantity,
    isAvailable: dbItem.is_available ?? true,
    imageUrl: dbItem.image_url,
    items,
    discount: dbItem.discount ?? 0,
    totalBasePrice: dbItem.total_base_price ?? 0,
    totalDiscountedPrice: dbItem.total_discounted_price ?? 0,
  };
};

// Mapper: Frontend → Database Insert (camelCase → snake_case)
const rentalKitToInsertDb = (
  item: Partial<Omit<RentalKit, 'id' | 'createdAt' | 'updatedAt'>>
): Database['public']['Tables']['rental_kits']['Insert'] => ({
  user_id: item.userId!,
  name: item.name!,
  description: item.description,
  category_id: item.categoryId,
  price: item.price,
  quantity: item.quantity,
  is_available: item.isAvailable,
  image_url: item.imageUrl,
  items: item.items ? JSON.stringify(item.items) : null,
  discount: item.discount,
  total_base_price: item.totalBasePrice,
  total_discounted_price: item.totalDiscountedPrice,
});

// Mapper: Frontend → Database Update (camelCase → snake_case)
const rentalKitToUpdateDb = (
  item: Partial<Omit<RentalKit, 'id' | 'createdAt' | 'updatedAt'>>
): Database['public']['Tables']['rental_kits']['Update'] => ({
  name: item.name,
  description: item.description,
  category_id: item.categoryId,
  price: item.price,
  quantity: item.quantity,
  is_available: item.isAvailable,
  image_url: item.imageUrl,
  items: item.items ? JSON.stringify(item.items) : undefined,
  discount: item.discount,
  total_base_price: item.totalBasePrice,
  total_discounted_price: item.totalDiscountedPrice,
});

// Explicit return type for the hook to satisfy strict typing and lint rules
type UseRentalKitsUnifiedResult = {
  kits: RentalKit[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<unknown>;
  createKit: (
    data: Partial<Omit<RentalKit, 'id' | 'createdAt' | 'updatedAt'>>
  ) => Promise<RentalKit>;
  updateKit: (
    args: { id: string; updates: Partial<Omit<RentalKit, 'id'>> }
  ) => Promise<RentalKit>;
  deleteKit: (id: string) => Promise<string>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
};

export const useRentalKitsUnified = (
  filters?: Record<string, string | number | boolean | null | undefined>
): UseRentalKitsUnifiedResult => {
  const { user } = useAuth();
  const queryKey = ['rental_kits', { userId: user?.id, ...filters }];

  const { 
    data: kits, 
    isLoading, 
    error, 
    refetch 
  } = useEntityList<RentalKit, 'rental_kits'> ({
    tableName: 'rental_kits',
    queryKey,
    dbEntityToEntityMapper: dbRentalKitToRentalKit,
    filters: { user_id: user?.id, ...filters },
  });

  const {
    createEntity,
    updateEntity: updateKit,
    deleteEntity: deleteKit,
    isCreating,
    isUpdating,
    isDeleting,
  } = useEntityOperations<RentalKit, 'rental_kits'> ({
    tableName: 'rental_kits',
    queryKey,
    dbEntityToEntityMapper: dbRentalKitToRentalKit,
    entityToInsertDbMapper: rentalKitToInsertDb,
    entityToUpdateDbMapper: rentalKitToUpdateDb,
  });

  // Ensure user_id is set on INSERT to satisfy RLS (WITH CHECK auth.uid() = user_id)
  const createKit = async (
    data: Partial<Omit<RentalKit, 'id' | 'createdAt' | 'updatedAt'>>
  ) => {
    if (!user?.id) {
      throw new Error('[useRentalKitsUnified] Cannot create kit: user is not authenticated');
    }
    // Attach userId if not provided by caller
    const payload = { userId: user.id, ...data };
    return createEntity(payload);
  };

  return {
    kits: kits || [],
    isLoading,
    error,
    refetch,
    createKit,
    updateKit,
    deleteKit,
    isCreating,
    isUpdating,
    isDeleting,
  };
};
